/**
 * Mock Data Manager
 *
 * Centralized persistence layer for all mock data.
 * Automatically saves/loads mock data to/from localStorage.
 *
 * Usage:
 * 1. Wrap your mock data array with createPersistedMockData()
 * 2. Data automatically persists across page refreshes
 * 3. Remove this file when backend API is ready
 */

type MockDataCollection = any[];

interface PersistedMockData<T> {
  data: T[];
  get: () => T[];
  set: (data: T[]) => void;
  update: (id: string, updates: Partial<T>) => T | undefined;
  add: (item: T) => T;
  remove: (id: string) => boolean;
  reset: () => void;
}

/**
 * Create a persisted mock data collection
 *
 * @param storageKey - Unique key for localStorage
 * @param defaultData - Default data to use if localStorage is empty
 * @returns Persisted data collection with helper methods
 */
export function createPersistedMockData<T extends { id: string }>(
  storageKey: string,
  defaultData: T[]
): PersistedMockData<T> {
  // Load from localStorage or use defaults
  function load(): { data: T[]; isNew: boolean } {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Mock data loaded (silent)
        return { data: parsed, isNew: false };
      }
    } catch (error) {
      console.error(`Failed to load ${storageKey} from localStorage:`, error);
    }
    // Mock data initialized (silent)
    return { data: [...defaultData], isNew: true };
  }

  // Save to localStorage
  function save(data: T[]): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
      // Mock data saved (silent)
    } catch (error) {
      console.error(`Failed to save ${storageKey} to localStorage:`, error);
    }
  }

  // Initialize data and save defaults if new
  const loaded = load();
  let persistedData = loaded.data;

  // Save default data to localStorage on first initialization
  if (loaded.isNew) {
    save(persistedData);
    // Mock data persisted (silent)
  }

  return {
    // Raw data access (returns reference, use with caution)
    data: persistedData,

    // Get current data (returns copy) - ALWAYS read fresh from localStorage
    get: () => {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Update in-memory cache to match localStorage
          persistedData = parsed;
          return [...parsed];
        }
      } catch (error) {
        console.error(`Failed to read ${storageKey} from localStorage:`, error);
      }
      // Fallback to in-memory data if localStorage read fails
      return [...persistedData];
    },

    // Set entire dataset
    set: (data: T[]) => {
      persistedData = [...data];
      save(persistedData);
    },

    // Update single item
    update: (id: string, updates: Partial<T>) => {
      // First refresh from localStorage to ensure we have latest data
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          persistedData = JSON.parse(stored);
        }
      } catch (e) {
        console.error(`[mockDataManager] Failed to refresh ${storageKey} before update:`, e);
      }

      const index = persistedData.findIndex((item) => item.id === id);
      console.log(`[mockDataManager] update(${storageKey}):`, {
        id,
        foundAtIndex: index,
        totalItems: persistedData.length,
        updatesKeys: Object.keys(updates)
      });

      if (index !== -1) {
        persistedData[index] = { ...persistedData[index], ...updates };
        save(persistedData);
        console.log(`[mockDataManager] Saved updated item:`, persistedData[index]);
        return persistedData[index];
      }
      console.warn(`[mockDataManager] Item not found for update: ${id}`);
      return undefined;
    },

    // Add new item
    add: (item: T) => {
      // PE-1453: Refresh from localStorage first to avoid data overwrites
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          persistedData = JSON.parse(stored);
        }
      } catch (e) {
        console.error(`[mockDataManager] Failed to refresh ${storageKey} before add:`, e);
      }
      persistedData.push(item);
      save(persistedData);
      return item;
    },

    // Remove item
    remove: (id: string) => {
      const index = persistedData.findIndex((item) => item.id === id);
      if (index !== -1) {
        persistedData.splice(index, 1);
        save(persistedData);
        return true;
      }
      return false;
    },

    // Reset to defaults
    reset: () => {
      persistedData = [...defaultData];
      save(persistedData);
      console.log(`🔄 Reset ${storageKey} to defaults (${defaultData.length} items)`);
    }
  };
}

/**
 * Clear all persisted mock data (useful for testing/debugging)
 * WARNING: This will delete ALL user data including custom configurations!
 */
export function clearAllMockData(): void {
  const keys = [
    'crm_roles',
    'crm_objects',
    'crm_workflows',
    'crm_users',
    'crm_field_permissions',
    'crm_email_templates',
    'crm_object_fields',
    'crm_page_layouts',
    'crm_record_pages',
    'crm_om_record_pages',
    'crm_tab_configurations',
    'crm_client_records',
    'crm_client_list_views',
    'crm_lead_records',
    'crm_contact_records',
    'crm_opportunity_records',
    'crm_case_records',
    'crm_task_records',
    'crm_event_records',
    'crm_report_records',
    'crm_approval_processes',
    'crm_approval_requests',
    'crm_processes',
    'crm_process_executions',
    // CyberSweep records (PE-747)
    'crm_paw_records',
    'crm_cs_audit_records',
    // Support tickets (PE-764)
    'crm_support_tickets',
    // Accounts (firms for pricebooks)
    'crm_account_records',
    'crm_account_list_views',
    // Products, Pricebooks, Invoices
    'crm_product_records',
    'crm_product_list_views',
    'crm_pricebook_records',
    'crm_pricebook_entries',
    'crm_client_price_overrides',
    'crm_invoice_records',
    'crm_invoice_list_views'
  ];

  const confirmation = window.confirm(
    '⚠️ WARNING: This will delete ALL user data including custom page layouts, record pages, and configurations!\n\n' +
    'Are you sure you want to continue?\n\n' +
    'Tip: Use window.crmBackup() to create a backup first!'
  );

  if (!confirmation) {
    console.log('❌ Clear cancelled by user');
    return;
  }

  keys.forEach(key => {
    try {
      localStorage.removeItem(key);
      console.log(`🗑️ Cleared ${key}`);
    } catch (error) {
      console.error(`Failed to clear ${key}:`, error);
    }
  });

  console.log('✨ All mock data cleared. Refresh page to reload defaults.');
  console.log('💡 To restore data, use: window.crmRestore()');
}

/**
 * Export to window for easy debugging in console
 */
if (typeof window !== 'undefined') {
  (window as any).clearMockData = clearAllMockData;
  console.log('💡 Debug command available: window.clearMockData()');
}
